import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { StaffOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { useParam } from '../lib/filters'
import { count, formatDateTime, fullName, humanise } from '../lib/format'

const ROLE_TONES: Record<string, string> = {
  admin: 'brand',
  sales: 'blue',
  coordinator: 'teal',
  driver: 'amber',
  warehouse: 'purple',
  pickup_counter: 'orange',
  security_guard: 'grey',
}

export function StaffPage() {
  const [show, setShow] = useParam<'active' | 'all'>('show', 'active', ['active', 'all'])

  const query = useQuery({
    queryKey: ['staff-manage'],
    staleTime: 10 * 60 * 1000,
    queryFn: () => api.get<StaffOut[]>('/staff/manage'),
  })

  const everyone = query.data ?? []
  const rows = show === 'active' ? everyone.filter((person) => person.is_active) : everyone
  const sorted = [...rows].sort((a, b) =>
    a.role === b.role
      ? fullName(a.first_name, a.last_name).localeCompare(fullName(b.first_name, b.last_name))
      : a.role.localeCompare(b.role),
  )

  return (
    <>
      <PageHeader title="Staff" subtitle="Accounts and their last sign-in">
        <div className="seg">
          <button className={show === 'active' ? 'on' : ''} onClick={() => setShow('active')}>
            Active
          </button>
          <button className={show === 'all' ? 'on' : ''} onClick={() => setShow('all')}>
            Everyone
          </button>
        </div>
      </PageHeader>

      <div className="content stack">
        <div className="card">
          <div className="card-head">
            <h2>Accounts</h2>
            <span className="hint">{count(sorted.length)} shown</span>
          </div>
          <div className="card-body flush">
            <Panel isLoading={query.isPending} error={query.error} isEmpty={sorted.length === 0}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Phone</th>
                      <th>HIKE user</th>
                      <th className="num">Last sign-in</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((person) => (
                      <tr key={person.id}>
                        <td className="strong">{fullName(person.first_name, person.last_name)}</td>
                        <td className="cell-sub">{person.username}</td>
                        <td>
                          <span className={`chip ${ROLE_TONES[person.role] ?? 'grey'}`}>
                            {humanise(person.role)}
                          </span>
                        </td>
                        <td className="cell-sub">{person.phone ?? '—'}</td>
                        <td className="cell-sub">{person.hike_user_id ?? '—'}</td>
                        <td className="num cell-sub">{formatDateTime(person.last_login_at)}</td>
                        <td>
                          {!person.is_active ? (
                            <span className="chip grey">Inactive</span>
                          ) : person.must_change_password ? (
                            <span className="chip amber">Must set password</span>
                          ) : (
                            <span className="chip green">Active</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>

        <div className="alert info">
          This view is read-only. Creating accounts, changing roles and resetting passwords are still
          done in the mobile app.
        </div>
      </div>
    </>
  )
}
